import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { config } from '../../utils/utils';
import { createOrderStatusChange } from '../orderStatusChange/api';
import { findOrder } from '../order/api';
import { findDashboardOrder } from '../dashboardOrders/api';

// Async thunk to fetch pending orders count
export const fetchPendingOrdersCount = createAsyncThunk(
  'pendingOrdersCount/fetchPendingOrdersCount',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem(config.AUTH_KEY);
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${config.BASE_URL}/pending-orders-count`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch pending orders count');
    }
  }
);

interface PendingOrdersCountState {
  count: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: PendingOrdersCountState = {
  count: 0,
  status: 'idle',
  error: null,
};

const pendingOrdersCountSlice = createSlice({
  name: 'pendingOrdersCount',
  initialState,
  reducers: {
    resetPendingOrdersCount: (state) => {
      state.count = 0;
      state.status = 'idle';
      state.error = null;
    },
    refreshPendingOrdersCount: (state) => {
      state.status = 'idle'; // Reset to idle to trigger refetch
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingOrdersCount.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchPendingOrdersCount.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.count = action.payload.data?.pendingOrdersCount || 0;
        state.error = null;
      })
      .addCase(fetchPendingOrdersCount.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        state.count = 0;
      })
      // Listen for order status changes and refresh count
      .addCase(createOrderStatusChange.fulfilled, (state) => {
        state.status = 'idle'; // Reset to idle to trigger refetch
      })
      // Listen for order list updates and refresh count
      .addCase(findOrder.fulfilled, (state) => {
        state.status = 'idle'; // Reset to idle to trigger refetch
      })
      // Listen for dashboard order updates and refresh count
      .addCase(findDashboardOrder.fulfilled, (state) => {
        state.status = 'idle'; // Reset to idle to trigger refetch
      });
  },
});

export const { resetPendingOrdersCount, refreshPendingOrdersCount } = pendingOrdersCountSlice.actions;

// Selector
export const selectPendingOrdersCount = (state: RootState) => state.pendingOrdersCount;

export default pendingOrdersCountSlice.reducer;
